import { useMemo } from 'react';
import Box from '@mui/material/Box';
import { sp } from '@/theme/mui';

// components
import { Accordion } from '@/components/atoms/Accordion/Accordion';
import { Button } from '@/components/atoms/Button/Button';
import { Title } from '@/components/atoms/Title/Title';

import { Editor } from '@/components/organisms/Editor/Editor';

/**
 * RecipientProfileAbout shows the profile of the care recipient.
 */
export const RecipientProfileAbout = ({
  className,
  name,
  keyInformationForm,
  nextOfKinForm,
  professionalContactsForm,
  answers,
  setAnswers,
  onSave,
  isEditing,
  isModify = false,
  onBack,
}: RecipientProfileAboutProps) => {
  // memos
  const SaveButton = useMemo(() => {
    if (!isModify) {
      return null;
    }
    return (
      <StyledButton
        variant={isEditing ? 'primary' : 'secondary'}
        onPress={onSave}
        ariaLabel={isEditing ? 'save' : 'edit'}
      >
        {isEditing ? 'Save' : 'Edit'}
      </StyledButton>
    );
  }, [isEditing, onSave, isModify]);

  return (
    <Wrapper className={className}>
      {onBack && (
        <Button
          ariaLabel='back'
          iconLeft='chevron-left'
          variant='secondary'
          size='sm'
          onPress={onBack}
        >
          Back
        </Button>
      )}
      <Title>Key Information</Title>
      <SupportText>
        {`${name || 'Your loved one'}'s`} key information for you to share with
        others in the support network when needed.
      </SupportText>
      <Accordion
        items={[
          {
            id: 'key-information',
            label: 'Key Information',
            content: keyInformationForm && (
              <>
                {SaveButton}
                <Editor
                  fields={keyInformationForm}
                  isActionsDisabled={true}
                  isFloatingActions={false}
                  value={answers}
                  isDisabled={!isEditing}
                  onChange={(val, key) =>
                    setAnswers?.({ ...answers, [key]: val[key] })
                  }
                />
              </>
            ),
          },
          {
            id: 'next-of-kin',
            label: 'Next of Kin',
            content: nextOfKinForm && (
              <>
                {SaveButton}
                <Editor
                  fields={nextOfKinForm}
                  isActionsDisabled={true}
                  isFloatingActions={false}
                  value={answers}
                  isDisabled={!isEditing}
                  onChange={(val, key) =>
                    setAnswers?.({ ...answers, [key]: val[key] })
                  }
                />
              </>
            ),
          },
          {
            id: 'professional-contacts',
            label: 'Professional Contacts',
            content: professionalContactsForm && (
              <>
                {SaveButton}
                <Editor
                  fields={professionalContactsForm}
                  isActionsDisabled={true}
                  isFloatingActions={false}
                  value={answers}
                  isDisabled={!isEditing}
                  onChange={(val, key) =>
                    setAnswers?.({ ...answers, [key]: val[key] })
                  }
                />
              </>
            ),
          },
        ]}
      />
    </Wrapper>
  );
};

const Wrapper = ({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) => (
  <Box className={className} sx={{ pb: sp[24] }}>
    {children}
  </Box>
);

const SupportText = ({ children }: { children?: React.ReactNode }) => (
  <Box sx={{ color: '#666667', typography: 'bodyMd' }}>{children}</Box>
);

const StyledButton = (props: any) => (
  <Box sx={{ mt: sp[24], ml: 'auto' }}>
    <Button {...props} />
  </Box>
);

interface RecipientProfileAboutProps {
  className?: string;
  name: string;
  keyInformationForm?: any;
  nextOfKinForm?: any;
  professionalContactsForm?: any;
  answers?: any;
  setAnswers?: (value: any) => void;
  careRecipient?: string;
  onSave?: () => void;
  isEditing?: boolean;
  isModify?: boolean;
  onBack?: () => void;
}

RecipientProfileAbout.displayName = 'RecipientProfileAbout';

export default RecipientProfileAbout;
